/* eslint-disable */
import { addMinutes, differenceInMinutes, isWithinInterval, startOfHour, subMinutes } from 'date-fns';
import { PatternObject, SeriesGanttDataOptions } from 'highcharts';
import cloneDeep from 'lodash.clonedeep';
import { difference, equals, intersection, isEmpty } from 'ramda';

import {
  ForecastProduction,
  CalculatedWorkContributionData,
  QueueChannelType,
  Queue,
  StaffingPerQueue,
  EslStaffingPerQueueInput,
} from '../../../graphql/components';
import { recordFromIds } from '../../../utils/array';
import {
  formatScheduleElementRGBAColor,
  getContrastColor,
  shadeColor,
  transformColorRGBAToObj,
} from '../../../utils/chart';
import { Agent } from '../../Agents/types';
import { DEFAULT_Y_AXIS_TICKS, MIN_15, Y_AXIS_TICKS_COUNT } from '../config';
import {
  ChartScheduleElement,
  MissingForecastPerQueue,
  ScheduleElementsContributionPerQueue,
  TransformedWorkContribution,
  TransformedProductionForecast,
  ElementIdsPerAgent,
  DaySettings,
  ProductionChartScheduleElement,
  TransformedProductionElementsWorkContribution,
  TransformProductionElementsWorkContributionData,
  TransformedStaffingPerQueue,
  SchedulingChartType,
} from '../types';
import { deriveChannelTypeFromElement } from './transformScheduleElementsData';
import { Values } from '../../../types';

export const FILL_PATTERN_SIZE = 32;

export interface MapScheduledStaffConfig {
  timelineEntry: TimelineItem;
  data: ChartScheduleElement[];
  startTimestamp: number;
  endTimestamp: number;
  elementIds: string[];
  agents: Record<string, Agent>;
}

export interface MapRequiredStaffConfig {
  timelineEntry: TimelineItem;
  data: ForecastProduction | null;
}

export interface TimelineItem {
  start: number;
  end: number;
  netStaff: number;
  requiredStaff: number;
  scheduledStaff: number;
  zeroValue: number; // so we can highlight 0 in the chart
}

export enum ChartDataKey {
  NET = 'netStaff',
  REQUIRED = 'requiredStaff',
  SCHEDULED = 'scheduledStaff',
  ZERO_VALUE = 'zeroValue',
}

type MapChartDataProps = {
  agents: Record<string, Agent>;
  endTimestamp: number;
  forecast: TransformedProductionForecast;
  schedulingChartType: Values<typeof SchedulingChartType>;
  interval?: number;
  productionScheduleElements: ProductionChartScheduleElement[];
  productionScheduleElementsMissingQueueIds: string[];
  productionElementsWorkContribution: TransformedProductionElementsWorkContribution;
  productiveElementIds: string[];
  queueIds: string[];
  queues: Record<string, Queue>;
  queuesStaffingOfProductionElements: TransformedStaffingPerQueue;
  scheduleElements: ChartScheduleElement[];
  startTimestamp: number;
  workContributionData: TransformedWorkContribution;
};

type MapInitialChartDataProps = {
  endTimestamp: number;
  forecast: TransformedProductionForecast;
  initialStaffingPerQueue: TransformedStaffingPerQueue;
  interval?: number;
  queueIds: string[];
  queuesStaffingOfProductionElements: TransformedStaffingPerQueue;
  startTimestamp: number;
};

type CalculatedScheduleData = {
  netStaff: Record<number, number>;
  requiredStaff: Array<[number, number]>;
  scheduledStaff: Array<[number, number]>;
};

export type CalculatedScheduleDataPerQueue = Record<string, CalculatedScheduleData>;

export const emptyAgentsData: ElementIdsPerAgent = {
  pinnable: [],
  pinned: [],
  selected: [],
  total: [],
};

export const transformProductionForecastData = (
  data: ForecastProduction[],
  queues: Record<string, Queue>,
  scheduleStartTime: number,
  scheduleEndTime: number
) => {
  const { transformedData, queueIds } = data.reduce(
    (reducedData, { queueId }) => {
      reducedData.transformedData[queueId] = {};
      reducedData.queueIds.push(queueId);

      return reducedData;
    },
    { transformedData: {} as TransformedProductionForecast, queueIds: [] as string[] }
  );

  const missingForecastIntervalsPerQueue: MissingForecastPerQueue = Object.keys(queues).reduce(
    (voiceQueues, queueId) => {
      if (queues[queueId].channelType === QueueChannelType.Voice) {
        voiceQueues[queueId] = {};
      }

      return voiceQueues;
    },
    {} as MissingForecastPerQueue
  );

  if (queueIds.length) {
    // forecastValues contain consistent amount of data - for same intervals, but with null values if forecast is not present
    data[0].forecastValues.forEach(({ dateTimeRange: { startDateTime } }, timestampIndex) => {
      const timestamp = new Date(startDateTime).getTime();
      const isIntervalWithinScheduleRange = isWithinInterval(timestamp, {
        start: scheduleStartTime,
        end: scheduleEndTime - 1,
      });

      queueIds.forEach((queueId, queueIndex) => {
        const forecastData = data[queueIndex].forecastValues[timestampIndex].value.staffingRequirements;
        if (typeof forecastData === 'number') {
          transformedData[queueId][timestamp] = forecastData;
        } else {
          // add only to queues of channel type other than Voice
          // filter out missing forecast values outside schedule range (when viewing schedule in different timezone)
          if (isIntervalWithinScheduleRange && missingForecastIntervalsPerQueue[queueId]) {
            missingForecastIntervalsPerQueue[queueId][timestamp] = true;
          }
        }
      });
    });
  }

  const missingOrIncompleteForecastQueueIds = Object.keys(missingForecastIntervalsPerQueue).filter(
    (queueId) => !isEmpty(missingForecastIntervalsPerQueue[queueId])
  );

  return { transformedData, missingOrIncompleteForecastQueueIds, missingForecastIntervalsPerQueue };
};

type AgentsContributionPerQueue = {
  [queueId: string]: {
    [timestamp: string]: number;
  };
};

export const transformWorkContributionData = (data: CalculatedWorkContributionData[]) =>
  data.reduce((reducedData, { queueId, workContributions }) => {
    workContributions.forEach(({ dateTimeRange, value: { agentWorkContributions, queueTraffic } }) => {
      const timestamp = new Date(dateTimeRange.startDateTime).getTime();

      if (reducedData[timestamp] === undefined) {
        reducedData[timestamp] = {};
      }

      reducedData[timestamp][queueId] = {
        agentsContribution: {},
        traffic: queueTraffic,
      };

      agentWorkContributions.forEach(({ agentId, value: workContribution }) => {
        reducedData[timestamp][queueId].agentsContribution[agentId] = workContribution;
      });
    });

    return reducedData;
  }, {} as TransformedWorkContribution);

export const transformProductionElementsWorkContributionData = (
  allData: TransformProductionElementsWorkContributionData[],
  agentsRecordObj: Record<string, Agent>
) =>
  allData.reduce((reducedAllData, { data, scheduleId }) => {
    data.forEach(({ queueId, workContributions }) => {
      workContributions.forEach(({ dateTimeRange, value: { agentWorkContributions, queueTraffic } }) => {
        const timestamp = new Date(dateTimeRange.startDateTime).getTime();

        if (reducedAllData[timestamp] === undefined) {
          reducedAllData[timestamp] = {};
        }

        if (reducedAllData[timestamp][scheduleId] === undefined) {
          reducedAllData[timestamp][scheduleId] = {};
        }

        reducedAllData[timestamp][scheduleId][queueId] = {
          agentsContribution: {},
          traffic: queueTraffic,
        };

        agentWorkContributions.forEach(({ agentId, value: workContribution }) => {
          if (agentsRecordObj[agentId]) {
            // we should restore only work contribution of relevant agents
            reducedAllData[timestamp][scheduleId][queueId].agentsContribution[agentId] = workContribution;
          }
        });
      });
    });

    return reducedAllData;
  }, {} as TransformedProductionElementsWorkContribution);

export const transformInitialStaffingPerQueueData = (initialData?: StaffingPerQueue[]) => {
  if (!initialData?.length) {
    return undefined;
  }

  const data: TransformedStaffingPerQueue = {};

  for (const { queueId, scheduleQueuesStaffing } of initialData) {
    data[queueId] = {};

    for (const { dateRange, staffing } of scheduleQueuesStaffing) {
      const timestamp = new Date(dateRange.startDateTime).getTime();
      data[queueId][timestamp] = staffing;
    }
  }

  return data;
};

// prepare data before sending to endpoint for calculation of estimated SLA
export const transformCalculatedQueuesStaffingData = (calculatedData?: CalculatedScheduleDataPerQueue) => {
  if (!calculatedData || !Object.keys(calculatedData).length) {
    return [];
  }

  const data: EslStaffingPerQueueInput[] = [];

  for (const [queueId, { scheduledStaff }] of Object.entries(calculatedData)) {
    const dataPerQueue: EslStaffingPerQueueInput = {
      queueId,
      scheduledStaffing: [],
    };

    for (const [startIntervalTimestamp, staffing] of scheduledStaff) {
      dataPerQueue.scheduledStaffing.push({
        staffing,
        startDateTime: new Date(startIntervalTimestamp).toISOString(),
      });
    }

    data.push(dataPerQueue);
  }

  return data;
};

export const mapInitialChartData = ({
  endTimestamp,
  forecast,
  initialStaffingPerQueue,
  interval = MIN_15,
  queuesStaffingOfProductionElements,
  queueIds,
  startTimestamp,
}: MapInitialChartDataProps) => {
  /**
   * queues staffing - work contribution of both draft elements
   * and production elements from other schedules should be preloaded from BE
   */
  const plotBandsData: Record<number, string[]> = {};
  const scheduleElementsContributionPerQueue: ScheduleElementsContributionPerQueue = {};

  const dataPerQueue: CalculatedScheduleDataPerQueue = {};
  const agentsContributionPerQueue: AgentsContributionPerQueue = {};

  // prepare scheduled data per queue
  // eslint-disable-next-line no-restricted-syntax
  for (const queueId of queueIds) {
    agentsContributionPerQueue[queueId] = {};
    dataPerQueue[queueId] = {
      netStaff: {},
      requiredStaff: [],
      scheduledStaff: [],
    };
  }

  let currentTimelineInterval = startTimestamp;
  let maxY = 0;

  while (currentTimelineInterval < endTimestamp) {
    let staffedTotal = 0;
    let requiredTotal = 0;

    // eslint-disable-next-line no-loop-func
    queueIds.forEach((queueId) => {
      const staffedFromCurrentDraft = initialStaffingPerQueue[queueId]?.[currentTimelineInterval] ?? 0;
      const staffedFromOtherProductionSchedules =
        queuesStaffingOfProductionElements[queueId]?.[currentTimelineInterval] ?? 0;
      const staffed = staffedFromCurrentDraft + staffedFromOtherProductionSchedules;
      const required = forecast[queueId]?.[currentTimelineInterval];

      const staffedTruncated = Math.floor(staffed * 100) / 100;
      const net = typeof required === 'number' ? Math.floor((staffedTruncated - required) * 100) / 100 : 0;
      staffedTotal += staffedTruncated;
      requiredTotal += required ?? 0;

      dataPerQueue[queueId].scheduledStaff.push([currentTimelineInterval, staffedTruncated]);
      // fallback value -1 should indicate missing forecast for given interval
      dataPerQueue[queueId].requiredStaff.push([currentTimelineInterval, required ?? -1]);

      agentsContributionPerQueue[queueId][currentTimelineInterval] = staffed;

      if (net !== 0) {
        dataPerQueue[queueId].netStaff[currentTimelineInterval] = net;

        if (!plotBandsData[currentTimelineInterval]) {
          plotBandsData[currentTimelineInterval] = [];
        }

        plotBandsData[currentTimelineInterval].push(queueId);
      }
    });

    const maxCurrentValue = Math.max(staffedTotal, requiredTotal);

    if (maxCurrentValue > maxY) {
      maxY = maxCurrentValue;
    }

    currentTimelineInterval += interval;
  }

  return {
    dataPerQueue,
    scheduleElementsContributionPerQueue,
    plotBandsData: Object.keys(plotBandsData).map((timestamp) => {
      const timestampAsNumber = Number(timestamp);

      return {
        start: timestampAsNumber,
        end: timestampAsNumber + interval,
        queueIds: plotBandsData[timestampAsNumber],
      };
    }),
    yAxisTicks: computeYAxisTicks(maxY, 0),
  };
};

export const mapChartData = ({
  agents,
  endTimestamp,
  forecast,
  schedulingChartType,
  interval = MIN_15,
  queuesStaffingOfProductionElements,
  productionScheduleElements,
  productionScheduleElementsMissingQueueIds,
  productionElementsWorkContribution,
  productiveElementIds,
  queueIds,
  queues,
  scheduleElements,
  startTimestamp,
  workContributionData,
}: MapChartDataProps) => {
  const intervalInMinutes = interval / 60 / 1000;
  /**
   * Steps:
   * 1. get through schedule items, make piles of intervals
   * 2. get through timeline, match required and staffed data (into 2 separate series per queue)
   */

  const plotBandsData: Record<number, string[]> = {};
  const scheduleElementsContributionPerQueue: ScheduleElementsContributionPerQueue = {};

  const useIntradayForecast = schedulingChartType === SchedulingChartType.Intraday;
  const usableQueueIds = useIntradayForecast ? [...queueIds, ...productionScheduleElementsMissingQueueIds] : queueIds;

  // prepare scheduled data per queue
  const { agentsContributionPerQueue, dataPerQueue } = usableQueueIds.reduce(
    (data, queueId) => {
      data.agentsContributionPerQueue[queueId] = useIntradayForecast
        ? {
            /** We still need to calculate contribution of production elements when using intraday forecast, as these data are not stored on BE side... 😢  */
          }
        : {
            /**
             * Queue staffing of production elements should be used as initial data,
             * as we no longer calculate contribution for them on FE side with every change of schedule draft
             * when using regular production forecast
             */
            ...queuesStaffingOfProductionElements?.[queueId],
          };
      data.dataPerQueue[queueId] = {
        netStaff: {},
        requiredStaff: [],
        scheduledStaff: [],
      };
      return data;
    },
    { dataPerQueue: {} as CalculatedScheduleDataPerQueue, agentsContributionPerQueue: {} as AgentsContributionPerQueue }
  );

  // filter out PTO production elements as they do not provide any contribution to queues staffing
  const filteredProductionElements = productionScheduleElements.filter((element) => !element.ptoRequestId);

  /**
   * For regular production forecast, productionScheduleElements should already have pre-computed contribution, but for
   * intraday forecast, work contribution should be calculated for both production and schedule's elements (production elements should have higher priority)
   */
  const elements = useIntradayForecast ? [...filteredProductionElements, ...scheduleElements] : scheduleElements;

  elements.forEach((element) => {
    if (
      !productiveElementIds.includes(element.scheduleElementId) ||
      // elements could exceed to previous/following date
      (!isWithinInterval(element.start, { start: startTimestamp, end: endTimestamp - 1 }) &&
        !isWithinInterval(element.end, { start: startTimestamp + 1, end: endTimestamp }))
    ) {
      return;
    }

    /**
     * When calculating contribution of production schedule elements from other schedules (using intraday forecast),
     * the contribution should be added only to those queues which were used in original production schedule,
     * otherwise it should be added to queues of current schedule
     */
    const productionElementQueueIds = (element as ProductionChartScheduleElement).queueIds;
    const isProductionScheduleElement = productionElementQueueIds !== undefined;
    const queueIdsToUse = useIntradayForecast && isProductionScheduleElement ? productionElementQueueIds : queueIds;

    const elementType = deriveChannelTypeFromElement(element);
    const sameChannelTypeQueueIds = queueIdsToUse.filter((queueId) => queues[queueId]?.channelType === elementType);
    const currentAgent = agents[element.agentId];
    const isAgentActive = currentAgent && currentAgent.active && !currentAgent.deleted;

    const startTimeOffset = differenceInMinutes(element.start, startOfHour(element.start));
    const startTimeQuarterOffset = startTimeOffset % intervalInMinutes;
    const startOfFirstCoveredInterval = subMinutes(element.start, startTimeQuarterOffset);

    const endTimeOffset = differenceInMinutes(element.end, startOfHour(element.end));
    const endTimeQuarterOffset = endTimeOffset % intervalInMinutes;
    const endOfLastCoveredInterval = addMinutes(
      element.end,
      (intervalInMinutes - endTimeQuarterOffset) % intervalInMinutes
    );

    let currentIntervalStart = startOfFirstCoveredInterval;
    let currentIntervalEnd = startOfFirstCoveredInterval;

    scheduleElementsContributionPerQueue[element.key] = {};

    const agentsQueuesIds = agents[currentAgent.id]?.queueIds ?? [];
    const agentsQueuesUsedWithinSchedule = intersection(agentsQueuesIds, sameChannelTypeQueueIds);

    while (currentIntervalStart < endOfLastCoveredInterval) {
      currentIntervalEnd = addMinutes(currentIntervalStart, intervalInMinutes);

      // TODO: revert back when having work contribution data of inactive/deleted agents provided by API
      // const isFutureEntry = isAfter(currentIntervalStart, Date.now());

      // if (isFutureEntry && !isAgentActive) {
      if (!isAgentActive) {
        currentIntervalStart = currentIntervalEnd;
        continue;
      }

      const currentIntervalStartMs = currentIntervalStart.getTime();

      const coveredPeriodInMinutes = differenceInMinutes(
        Math.min(element.end, currentIntervalEnd.getTime()),
        Math.max(element.start, currentIntervalStartMs)
      );
      const coveredPartOfPeriod = coveredPeriodInMinutes / intervalInMinutes;

      const { alreadyOverStaffedQueues, queuesWithMissingForecast } = agentsQueuesUsedWithinSchedule.reduce(
        (data, queueId) => {
          const currentForecast = forecast[queueId]?.[currentIntervalStartMs];

          /**
           * Note: Chat and Email channels doesn't need to contain forecast!
           * However, they should still take part on contribution.
           */
          if (queues[queueId].channelType === QueueChannelType.Voice && !currentForecast) {
            data.queuesWithMissingForecast.push(queueId);
          } else if (agentsContributionPerQueue[queueId][currentIntervalStartMs] >= currentForecast) {
            data.alreadyOverStaffedQueues.push(queueId);
          }

          return data;
        },
        { alreadyOverStaffedQueues: [] as string[], queuesWithMissingForecast: [] as string[] }
      );

      const queuesThatShouldNotParticipateOnContribution = [...alreadyOverStaffedQueues, ...queuesWithMissingForecast];

      const restAvailableQueues = difference(
        agentsQueuesUsedWithinSchedule,
        queuesThatShouldNotParticipateOnContribution
      );

      let queuesToFillWithContribution: string[] = [];
      let queuesToTakeContributionsFrom: string[] = [];

      /**
       * Decide the work-contribution (re)distribution strategy. All
       * of them share similar logic, but operates on different queues.
       */
      if (restAvailableQueues.length) {
        // move contributions from over-staffed queues to under-staffed
        queuesToFillWithContribution = restAvailableQueues;
        queuesToTakeContributionsFrom = queuesThatShouldNotParticipateOnContribution;
      } else if (queuesWithMissingForecast.length && alreadyOverStaffedQueues.length) {
        // move contributions from missing forecast queues to over-staffed queues, no under-staff queues left
        queuesToFillWithContribution = alreadyOverStaffedQueues;
        queuesToTakeContributionsFrom = queuesWithMissingForecast;
      } else {
        /**
         * Just use pre-calculated contributions from backend.
         *
         * This scenario lefts array queuesToTakeContributionsFrom empty on purpose
         * as we don't want to take any contribution from other queues. This will
         * result, later on in the code, in totalWorkContributionAddition equal to
         * 0 and therefore keeps the value of coefficient named ratio a 0 as well
         * - thus the code stays the same as it is for all other scenarios.
         */
        queuesToFillWithContribution = agentsQueuesUsedWithinSchedule;
      }

      // When using intraday forecast, contribution of prod elements should be retrieved from other source data
      const workContributionSource =
        useIntradayForecast && isProductionScheduleElement
          ? productionElementsWorkContribution?.[currentIntervalStartMs]?.[
              (element as ProductionChartScheduleElement).sourceScheduleId!
            ]
          : workContributionData?.[currentIntervalStartMs];

      const sumOfAvailableQueueTraffics = queuesToFillWithContribution.reduce(
        (value, queueId) => value + (workContributionSource?.[queueId]?.traffic ?? 0),
        0
      );

      const totalWorkContributionAddition = queuesToTakeContributionsFrom.reduce(
        (value, queueId) => value + (workContributionSource?.[queueId]?.agentsContribution[currentAgent.id] ?? 1),
        0
      );

      queuesToFillWithContribution.forEach((queueId) => {
        const queueIntervalWorkContributions = workContributionSource?.[queueId];
        const defaultWorkContribution = queueIntervalWorkContributions?.agentsContribution[currentAgent.id] ?? 1;

        let ratio: number = 0.0;
        if (sumOfAvailableQueueTraffics > 0) {
          // divide the amount of addition of work contribution proportionally based on queues traffic
          ratio = (queueIntervalWorkContributions?.traffic ?? 0) / sumOfAvailableQueueTraffics;
        } else if (totalWorkContributionAddition > 0) {
          // divide the amount of addition of work contribution equally to all rest queues
          ratio = 1.0 / queuesToFillWithContribution.length;
        }

        const workContributionAddition = totalWorkContributionAddition * ratio;
        const totalWorkContribution = coveredPartOfPeriod * (defaultWorkContribution + workContributionAddition);

        if (agentsContributionPerQueue[queueId][currentIntervalStartMs] === undefined) {
          agentsContributionPerQueue[queueId][currentIntervalStartMs] = 0;
        }
        agentsContributionPerQueue[queueId][currentIntervalStartMs] += totalWorkContribution;

        if (scheduleElementsContributionPerQueue[element.key][queueId] === undefined) {
          scheduleElementsContributionPerQueue[element.key][queueId] = {};
        }
        scheduleElementsContributionPerQueue[element.key][queueId][currentIntervalStartMs] = totalWorkContribution;
      });

      currentIntervalStart = currentIntervalEnd;
    }
  });

  let currentTimelineInterval = startTimestamp;
  let maxY = 0;

  while (currentTimelineInterval < endTimestamp) {
    let staffedTotal = 0;
    let requiredTotal = 0;

    queueIds.forEach((queueId) => {
      const staffed = agentsContributionPerQueue[queueId][currentTimelineInterval] ?? 0;
      const required = forecast[queueId]?.[currentTimelineInterval];

      const staffedTruncated = Math.floor(staffed * 100) / 100;
      const net = typeof required === 'number' ? Math.floor((staffedTruncated - required) * 100) / 100 : 0;
      staffedTotal += staffedTruncated;
      requiredTotal += required ?? 0;

      dataPerQueue[queueId].scheduledStaff.push([currentTimelineInterval, staffedTruncated]);
      // fallback value -1 should indicate missing forecast for given interval
      dataPerQueue[queueId].requiredStaff.push([currentTimelineInterval, required ?? -1]);

      if (net !== 0) {
        dataPerQueue[queueId].netStaff[currentTimelineInterval] = net;

        if (!plotBandsData[currentTimelineInterval]) {
          plotBandsData[currentTimelineInterval] = [];
        }

        plotBandsData[currentTimelineInterval].push(queueId);
      }
    });

    const maxCurrentValue = Math.max(staffedTotal, requiredTotal);

    if (maxCurrentValue > maxY) {
      maxY = maxCurrentValue;
    }

    currentTimelineInterval += interval;
  }

  return {
    dataPerQueue,
    scheduleElementsContributionPerQueue,
    plotBandsData: Object.keys(plotBandsData).map((timestamp) => {
      const timestampAsNumber = Number(timestamp);

      return {
        start: timestampAsNumber,
        end: timestampAsNumber + interval,
        queueIds: plotBandsData[timestampAsNumber],
      };
    }),
    yAxisTicks: computeYAxisTicks(maxY, 0),
  };
};

// computation requires maximum value >= 0 and minimum value <= 0 and roundBy value > 0
export const computeYAxisTicks = (
  maximum: number,
  minimum: number,
  countOfTicks = Y_AXIS_TICKS_COUNT,
  roundBy = 2
): number[] => {
  if (maximum === 0 && minimum === 0) return DEFAULT_Y_AXIS_TICKS;

  // round value to nearest multiple of roundBy - 2 by default
  const delta = Math.ceil((maximum + Math.abs(minimum)) / roundBy) * roundBy;
  const numberOfIntervals = countOfTicks - 1;

  let quarterOfDelta = Math.ceil(delta / numberOfIntervals);
  let firstTick: number;

  if (minimum >= 0) {
    firstTick = 0;
  } else {
    const coefficient = Math.floor(minimum / quarterOfDelta);
    firstTick = quarterOfDelta * coefficient;

    if (firstTick + numberOfIntervals * quarterOfDelta < maximum) {
      quarterOfDelta += 1;
      firstTick += coefficient;
    }
  }

  const ticks = [...Array(countOfTicks)].map((_, index) => firstTick + index * quarterOfDelta);

  return ticks;
};

interface GetScheduleElementsForDateRangeOptions<T extends ChartScheduleElement> {
  scheduleElements: T[];
  startTimestamp: number;
  endTimestamp: number;
}

export const elementInRange = (startTimestamp: number, endTimestamp: number, element: ChartScheduleElement) => {
  // elements could exceed to previous/following date
  return (
    isWithinInterval(element.start, { start: startTimestamp, end: endTimestamp - 1 }) ||
    isWithinInterval(element.end, { start: startTimestamp + 1, end: endTimestamp })
  );
};

export const getScheduleElementsNotInRange = <T extends ChartScheduleElement>({
  scheduleElements,
  startTimestamp,
  endTimestamp,
}: GetScheduleElementsForDateRangeOptions<T>): T[] =>
  // TODO 07.12.2021 (Salamon) - modify this to support elements that are longer than one day.
  scheduleElements.filter((element) => !elementInRange(startTimestamp, endTimestamp, element));

export const getScheduleElementsForDateRange = <T extends ChartScheduleElement>({
  scheduleElements,
  startTimestamp,
  endTimestamp,
}: GetScheduleElementsForDateRangeOptions<T>): T[] =>
  // TODO 07.12.2021 (Salamon) - modify this to support elements that are longer than one day.
  scheduleElements.filter((element) => elementInRange(startTimestamp, endTimestamp, element));

export const getWorkingAgents = (agentsIds: string[], elements: ChartScheduleElement[]) =>
  agentsIds.filter((agentId) => !!elements.filter((el) => !el.paidTimeOff).find((el) => el.agentId === agentId));

export const mapNotAssignedAgentSeriesData = (
  notAssignedAgentIds: string[],
  visibleAgentIds: string[],
  start: number,
  end: number
): SeriesGanttDataOptions[] =>
  notAssignedAgentIds.reduce<SeriesGanttDataOptions[]>((seriesData, agentId) => {
    const y = visibleAgentIds.findIndex((id) => id === agentId);

    if (y !== -1) {
      seriesData.push({
        end,
        start,
        y,
        // @ts-expect-error: incompatible type 😥
        className: 'gantt-chart-not-assigned-agents-series',
        color: {
          pattern: {
            path: {
              d: 'M -3, 33 L 3, 39 M -3, 25 L 11, 39 M -3, 17 L 19, 39 M -3, 9 L 27, 39 M -3, 1 L 35, 39 M -3, -7 L 43, 39 M -3, -15 L 51, 39 M -3, -23 L 59, 39 M -3, -31 L 67, 39 M -3, -39 L 75, 39 z',
              stroke: '#333',
              strokeWidth: 3,
              fill: '#fff',
            },
            key: `not-assigned-agent-${agentId}`,
            opacity: 0.3,
            height: FILL_PATTERN_SIZE,
            width: FILL_PATTERN_SIZE,
          },
        },
      });
    }
    return seriesData;
  }, []);

export type ProductionScheduleElementPoint = SeriesGanttDataOptions & {
  color: {
    pattern: Partial<PatternObject['pattern']>;
    color: string;
  };
  defaultColor: string;
};

export const mapProductionScheduleData = (
  elements: ProductionChartScheduleElement[]
): ProductionScheduleElementPoint[] =>
  elements.map((element) => {
    const color = transformColorRGBAToObj(element.color);
    const contrastColor = getContrastColor(color);
    let stripeColor = formatScheduleElementRGBAColor(color, 0.7);

    if (contrastColor.green === 0 || contrastColor.blue === 0 || contrastColor.red === 0) {
      // make stripe color darker
      stripeColor = formatScheduleElementRGBAColor(shadeColor(color, -30));
    }

    return {
      ...element,
      color: {
        pattern: {
          path: {
            d: 'M -3, 33 L 3, 39 M -3, 25 L 11, 39 M -3, 17 L 19, 39 M -3, 9 L 27, 39 M -3, 1 L 35, 39 M -3, -7 L 43, 39 M -3, -15 L 51, 39 M -3, -23 L 59, 39 M -3, -31 L 67, 39 M -3, -39 L 75, 39 z',
            stroke: element.color,
            strokeWidth: 3,
            fill: stripeColor,
          },
          key: `production-schedule-element-${element.key}`,
          height: FILL_PATTERN_SIZE,
          width: FILL_PATTERN_SIZE,
        },
        color: element.color,
      },
      defaultColor: element.color,
    };
  });

export const computeElementIdsPerAgents = (
  agentIds: string[],
  elements: ChartScheduleElement[],
  daySettings: Record<number, DaySettings>
) => {
  const agentsData = recordFromIds(agentIds, emptyAgentsData);
  const inOfficeAgentsPerDayIndex: Record<number, Set<string>> = {};

  const extraDayIndex = Object.keys(daySettings).length;
  const extendedDaySettings = cloneDeep(daySettings);
  extendedDaySettings[extraDayIndex] = cloneDeep(daySettings[extraDayIndex - 1]);

  let totalPinnableElementIds = [] as string[];

  elements.forEach(({ agentId, key, pinnable, pinned, selected, shift, dayIndexes }) => {
    agentsData[agentId]?.total.push(key);

    if (selected) {
      agentsData[agentId]?.selected.push(key);
    }

    if (pinned) {
      agentsData[agentId]?.pinned.push(key);
    }

    if (pinnable) {
      agentsData[agentId]?.pinnable.push(key);
      totalPinnableElementIds.push(key);
    }

    if (shift) {
      dayIndexes.forEach((dayIndex) => {
        if (extendedDaySettings[dayIndex]?.agents[agentId]?.remote === false) {
          if (!inOfficeAgentsPerDayIndex[dayIndex]) {
            inOfficeAgentsPerDayIndex[dayIndex] = new Set();
          }

          inOfficeAgentsPerDayIndex[dayIndex].add(agentId);
        }
      });
    }
  });

  return {
    totalPinnableElementIds,
    elementIdsPerAgent: agentsData,
    inOfficeAgentsPerDayIndex,
  };
};

/**
 * Creates diff between `previous` and `next` objects. Change is determined only for keys (object path) with same type,
 * otherwise value from `next` object is propagated to result `diff` object.
 *
 * Objects are checked recursively until primitive values are reached and evaluated.
 *
 * Arrays are checked on index basis which means types of previous[Array[index]] === next[Array[index]]
 */
export const differenceInValues = <T>(previous: T, next: T, shouldIncludeIds = true): any => {
  const typeOfPrevious = typeof previous;
  const typeOfNext = typeof next;

  if (typeOfPrevious !== typeOfNext) {
    // mismatch on types, return new object
    return next;
  }

  switch (typeOfPrevious) {
    case 'object': {
      break;
    }
    case 'function': {
      /**
       * Function equality needs to be expanded in order to correctly detect whether given function was changed or not.
       * For now, always return new one.
       */
      return next;
    }
    case 'undefined': {
      return undefined;
    }
    default: {
      // bigint, boolean, number, string, symbol
      return equals(previous, next) ? undefined : next;
    }
  }

  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/typeof#typeof_null
  if (previous == null) {
    return next;
  }

  if (next == null) {
    return undefined;
  }

  // objects, dates, arrays, ... . With custom overridden prototypes left.

  switch (Object.prototype.toString.call(next)) {
    case '[object Array]': {
      const partialDiffs = (next as unknown as Array<any>)
        .map((element, index) =>
          differenceInValues((previous as unknown as Array<any>)?.[index], element, shouldIncludeIds)
        )
        .filter((element) => !!element);

      if (!partialDiffs.length) {
        return undefined;
      }

      return partialDiffs;
    }

    case '[object Date]': {
      return (previous as unknown as Date).getTime() !== (next as unknown as Date).getTime() ? next : undefined;
    }

    case '[object Object]': {
      const partials = Object.keys(next).reduce((diff, key) => {
        const typedKey = key as keyof T;

        const differenceOnKey = differenceInValues(previous[typedKey], next[typedKey], shouldIncludeIds);

        if (differenceOnKey === undefined || isEmpty(differenceOnKey)) {
          return diff;
        }

        return {
          ...diff,
          [typedKey]: differenceOnKey,
        };
      }, {} as Partial<T>);

      checkId(next, partials, shouldIncludeIds);

      return isEmpty(partials) ? undefined : partials;
    }

    default: {
      // other objects eg: RegExp, Math, custom overridden values. Not necessary for current implementation.
      return undefined;
    }
  }
};

const checkId = (next: any, partials: any, shouldIncludeIds: boolean) => {
  if (shouldIncludeIds && next.id) {
    partials.id = next.id;
  }
};
